import { useCallback, useEffect, useRef, useState } from "react";

import {
  markCartReminderSeen,
  readCartReminderSeen,
} from "./cart-reminder.client";
import {
  CART_REMINDER_AUTO_DISMISS_MS,
  shouldShowCartReminder,
} from "./cart-reminder.logic";
import type { UseCartReminderOptions, UseCartReminderResult } from "./types";

/**
 * SFCC-12333 returning-shopper cart reminder. Decides once, on the first
 * basket resolve of the session: the flag is written whether or not the
 * reminder shows, so items added later in the session never trigger it, and a
 * refresh or soft navigation never re-triggers it. Closes on the X / CTA
 * (`dismiss`) or on its own after `CART_REMINDER_AUTO_DISMISS_MS`, a timer
 * held while the shopper hovers or focuses it; there is deliberately no
 * outside-click close.
 */
export function useCartReminder({
  hasItems,
  excluded,
}: UseCartReminderOptions): UseCartReminderResult {
  const [visible, setVisible] = useState(false);
  const [held, setHeld] = useState(false);
  // Guards the decision for this mount even if the cookie write was refused.
  const decidedRef = useRef(false);

  useEffect(() => {
    if (decidedRef.current || hasItems === undefined) return;
    decidedRef.current = true;
    const seenThisSession = readCartReminderSeen();
    markCartReminderSeen();
    if (shouldShowCartReminder({ seenThisSession, hasItems, excluded })) {
      setVisible(true);
    }
  }, [hasItems, excluded]);

  useEffect(() => {
    if (!visible || held) return;
    const timer = setTimeout(
      () => setVisible(false),
      CART_REMINDER_AUTO_DISMISS_MS,
    );
    return () => clearTimeout(timer);
  }, [visible, held]);

  // Soft-navigating onto an excluded route (the CTA's own target) ends it.
  useEffect(() => {
    if (excluded) setVisible(false);
  }, [excluded]);

  const dismiss = useCallback(() => setVisible(false), []);
  const hold = useCallback(() => setHeld(true), []);
  const release = useCallback(() => setHeld(false), []);

  return { visible, dismiss, hold, release };
}
